import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { systemAPI } from '../services/api';
import {
  DEFAULT_TIME_ZONE,
  formatDate,
  formatDateTime,
  formatDateTimeShort,
  formatTimeOfDay,
} from '../utils/datetime';

/**
 * 上次从服务端拿到的时区，只作为**暖启动**用：刷新页面时先按它渲染，避免首屏闪一下
 * 错的时间；真正的真相始终是服务端那一份。
 */
const WARM_CACHE_KEY = 'systemTimeZone';

interface TimezoneContextType {
  /** 当前生效的系统时区，例如 `Asia/Shanghai`。 */
  timeZone: string;
  /** 改系统时区。这是部署级设置，会同时影响签到日界线和 cron 计算。 */
  setTimeZone: (next: string) => Promise<void>;
  /** 是否已经从服务端确认过（false 时显示的是暖缓存或默认值）。 */
  loaded: boolean;
  /** 浏览器自己的时区，设置页用来提示"和本机不一致"。 */
  browserTimeZone: string;
  formatDateTime: (value: unknown, fallback?: string) => string;
  formatDate: (value: unknown, fallback?: string) => string;
  formatTimeOfDay: (value: unknown, fallback?: string) => string;
  /** 紧凑形式 `09-04 04:00`，给空间小的卡片副标题。 */
  formatDateTimeShort: (value: unknown, fallback?: string) => string;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

function detectBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

function readWarmCache(): string {
  try {
    return localStorage.getItem(WARM_CACHE_KEY) || DEFAULT_TIME_ZONE;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

function writeWarmCache(name: string): void {
  try {
    localStorage.setItem(WARM_CACHE_KEY, name);
  } catch {
    // 隐私模式下写不进去，只是下次刷新会先按默认值渲染，不影响功能。
  }
}

/**
 * 时间显示时区。**部署级配置，存在服务端**，不是每个浏览器各自的偏好。
 *
 * 库里存的时间一律是 UTC；这个设置决定所有时间按哪个时区显示，以及后端怎么判签到日、
 * 怎么算 cron。默认北京时间。
 */
export const TimezoneProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [timeZone, setTimeZoneState] = useState<string>(readWarmCache);
  const [loaded, setLoaded] = useState(false);
  const browserTimeZone = useMemo(detectBrowserTimeZone, []);

  useEffect(() => {
    let cancelled = false;
    systemAPI
      .getTimezone()
      .then((res) => {
        const name = res.data?.timezone;
        if (cancelled || !name) return;
        setTimeZoneState(name);
        writeWarmCache(name);
      })
      .catch(() => {
        // 拿不到就继续用暖缓存/默认值渲染。时间显示不该因为一次请求失败而消失。
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setTimeZone = useCallback(async (next: string) => {
    const res = await systemAPI.setTimezone(next);
    const name = res.data?.timezone || next;
    setTimeZoneState(name);
    writeWarmCache(name);
  }, []);

  const value = useMemo<TimezoneContextType>(
    () => ({
      timeZone,
      setTimeZone,
      loaded,
      browserTimeZone,
      formatDateTime: (v, fallback) => formatDateTime(v as never, timeZone, fallback),
      formatDate: (v, fallback) => formatDate(v as never, timeZone, fallback),
      formatTimeOfDay: (v, fallback) => formatTimeOfDay(v as never, timeZone, fallback),
      formatDateTimeShort: (v, fallback) => formatDateTimeShort(v as never, timeZone, fallback),
    }),
    [timeZone, setTimeZone, loaded, browserTimeZone],
  );

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};

/**
 * 没有 Provider 时的兜底：按默认时区（北京）格式化。
 *
 * 这里刻意**不抛异常**，和 AuthContext 不一样。少了 AuthProvider 是真的坏了——
 * 拿不到登录态就没法决定渲染什么；而少了 TimezoneProvider 只意味着"用默认时区"，
 * 那恰好就是它本来的默认值。为此让一个组件在测试里渲染不出来是不划算的。
 */
const FALLBACK: TimezoneContextType = {
  timeZone: DEFAULT_TIME_ZONE,
  setTimeZone: async () => {},
  loaded: false,
  browserTimeZone: DEFAULT_TIME_ZONE,
  formatDateTime: (v, fallback) => formatDateTime(v as never, DEFAULT_TIME_ZONE, fallback),
  formatDate: (v, fallback) => formatDate(v as never, DEFAULT_TIME_ZONE, fallback),
  formatTimeOfDay: (v, fallback) => formatTimeOfDay(v as never, DEFAULT_TIME_ZONE, fallback),
  formatDateTimeShort: (v, fallback) =>
    formatDateTimeShort(v as never, DEFAULT_TIME_ZONE, fallback),
};

export const useTimezone = (): TimezoneContextType => {
  return useContext(TimezoneContext) ?? FALLBACK;
};
